import { useEffect } from 'react';
import { useShallow } from 'zustand/shallow';

import { useBatchedMeshEventHandler } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useBatchedMeshEventHandler';
import { useBatchedMeshInitialization } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useBatchedMeshInitialization';
import { useCameraLodSyncHandler } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useCameraLodSyncHandler';
import { useMaterialUpdateHandler } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useMaterialUpdateHandler';
import { useShadowsUpateHandler } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useShadowsUpateHandler';
import { usePrepareManualConnectorDrag } from 'app/modules/wallforming/components/setting-container/hooks/usePrepareManualConnectorDrag';
import { usePrepareManualCornerInsertion } from 'app/modules/wallforming/components/setting-container/hooks/usePrepareManualCornerInsertion';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

/**
 * Responsibilities:
 * - Initializes and manages the batched mesh objects for the main scene.
 * - Handles material and shadow updates for the batched meshes.
 * - Attaches event handlers for pointer interactions (down, over, out) on the batched meshes.
 * - Handles LOD updates for the batched meshes.
 * - Retrieves and renders the batched mesh objects from the placement store.
 * - Renders the physical, sheet and holes batched mesh primitive.
 */
export const BatchedMeshHandler = () => {
    useBatchedMeshInitialization({ isMainCanvas: true });
    useMaterialUpdateHandler({ isMainCanvas: true });
    useShadowsUpateHandler({ isMainCanvas: true });
    useCameraLodSyncHandler({ isMainCanvas: true });

    const batchedMeshes = usePlacementStore(useShallow((state) => state.batchedMeshes));
    const handleOnArticleContextMenu = usePlacementStore(useShallow((state) => state.handleOnArticleContextMenu));

    const { handleOnPointerDown, handleOnPointerMove, handleOnPointerOver, handleOnPointerOut } =
        useBatchedMeshEventHandler();

    const prepareManualConnectorDrag = usePrepareManualConnectorDrag();
    const prepareManualCornerInsertion = usePrepareManualCornerInsertion();

    useEffect(() => {
        usePlacementStore.setState({ prepareManualConnectorDrag });
    }, [prepareManualConnectorDrag]);

    useEffect(() => {
        usePlacementStore.setState({ prepareManualCornerInsertion });
    }, [prepareManualCornerInsertion]);

    if (!batchedMeshes || !batchedMeshes.physical) {
        return null;
    }

    return (
        <>
            <primitive
                object={batchedMeshes.physical}
                onContextMenu={handleOnArticleContextMenu}
                onPointerDown={handleOnPointerDown}
                onPointerLeave={handleOnPointerOut}
                onPointerMove={handleOnPointerMove}
                onPointerOver={handleOnPointerOver}
            />
            {batchedMeshes.sheet && <primitive object={batchedMeshes.sheet} />}
            {batchedMeshes.holes && <primitive object={batchedMeshes.holes} />}
        </>
    );
};
